import { memo, useCallback, useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  IconButton,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Delete, ExpandMore } from '@mui/icons-material'
import { CenteringBox } from 'components/CenteringBox'
import { FileUpload } from 'components/FileUpload'
import { DeepPartial, useFieldArray, useFormContext } from 'react-hook-form'
import { ETenderRequirementType } from 'types/tender/tenderRequirements'
import { ILot } from 'types/lots'
import { useTranslation } from 'react-i18next'
import { useDeleteTenderRequirementItemMutation } from 'services/tender/tenderRequirementItem'
import { isDisabled } from '@testing-library/user-event/dist/utils'

type Props = {
  type: ETenderRequirementType
  isSubmissionTimePassed: boolean
}

const DocumentRequirementItemComponent = ({
  type,
  isSubmissionTimePassed,
}: Props) => {
  const { t } = useTranslation()
  const isGeneral = type === ETenderRequirementType.GENERAL
  const index = isGeneral ? 0 : 1
  const [isDisabled, setIsDisabled] = useState(false)

  const [deleteTenderRequirementItem] = useDeleteTenderRequirementItemMutation()

  const { register, control, setValue, watch } = useFormContext<DeepPartial<ILot>>()

  const documentRequirements = watch('documentRequirements')

  const documentRequirementsItem = watch(
    `documentRequirements.${index}.documentRequirementItems`
  )
  const { fields, append, remove } = useFieldArray({
    control,
    name: `documentRequirements.${index}.documentRequirementItems`,
  })

  useEffect(() => {
    setIsDisabled(isSubmissionTimePassed)
  }, [isSubmissionTimePassed])

  const handleAddClick = useCallback(() => {
    append({ criteria: '', eligibility: '' })
  }, [append])

  return (
    <Accordion sx={{ mt: 1 }} defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMore color="secondary" />}
        sx={{
          backgroundColor: (theme) => theme.palette.primary.main,
          color: 'white',
          overflow: 'hidden',
        }}
      >
        <Typography sx={{ width: '50%', flexShrink: 0 }}>
          {t(isGeneral ? 'legal-docs' : 'speciality')}
        </Typography>
        <Typography>
          {fields?.length || 0} {t(isGeneral ? 'doc' : 'criterion')}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {fields?.map((item, idx) => {
          return (
            <Box key={item.id} display="flex" gap={2} mb={2}>
              <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                <TextField
                  fullWidth
                  label={t('requirements')}
                  multiline
                  minRows={1}
                  variant="outlined"
                  {...register(
                    `documentRequirements.${index}.documentRequirementItems.${idx}.criteria`
                  )}
                  disabled={isDisabled}
                />
              </FormControl>
              <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                <TextField
                  fullWidth
                  label={t('notes-about-pricing')}
                  multiline
                  minRows={1}
                  variant="outlined"
                  {...register(
                    `documentRequirements.${index}.documentRequirementItems.${idx}.eligibility`
                  )}
                  disabled={isDisabled}
                />
              </FormControl>

              <Box display="flex" alignItems="center" gap={0.5}>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    if (documentRequirementsItem) {
                      deleteTenderRequirementItem({
                        documentId:
                          documentRequirementsItem?.[idx]?.documentRequirementId,
                        id: documentRequirementsItem?.[idx]?.id,
                      })
                    }
                    remove(idx)
                  }}
                  disabled={isDisabled}
                >
                  <Delete />
                </IconButton>
              </Box>
            </Box>
          )
        })}

        <CenteringBox mb={2}>
          <Button
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={handleAddClick}
            disabled={isDisabled}
          >
            <Add />
          </Button>
        </CenteringBox>

        <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
          <TextField
            fullWidth
            label={t('extra-info-short')}
            multiline
            minRows={4}
            variant="outlined"
            {...register(`documentRequirements.${index}.extraInfo`)}
            disabled={isDisabled}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
          <FileUpload
            text={t('extra-document')}
            handleFile={(file) => {
              setValue(`documentRequirements.${index}.extraFileId`, file.id)
            }}
            fileId={
              documentRequirements
                ? documentRequirements[index]?.extraFileId ?? undefined
                : undefined
            }
            onDelete={() =>
              setValue(`documentRequirements.${index}.extraFileId`, null)
            }
            isDisabled={isDisabled}
          />
        </FormControl>
      </AccordionDetails>
    </Accordion>
  )
}

export const DocumentRequirementItem = memo(DocumentRequirementItemComponent)
